// @flow strict

import React, { useCallback, useEffect, useState } from "react";
import { useServiceStore } from "../store/serviceStore";
import { CiCalendar } from "react-icons/ci";
import "react-calendar/dist/Calendar.css";
import { motion, AnimatePresence } from "framer-motion";
import ReservationServiceCalendar from "../components/ReservationServiceCalendar";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getServiceById } from "../api/serviceApi";
import Breadcrumb from "../components/Breadcrumb";
import axiosToken from "../api/axiosClient";
import { toast } from "react-toastify";
import useCartStore from "../store/cartStore";
import ReservationModal from "../components/ReservationModal";

export default function ServicePage() {
  const [fetchingData, setFetchingData] = useState(true);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState("");

  const { getServiceById: getStoreService } = useServiceStore();
  const { service_id } = useParams(); // récupère l'id dynamique depuis l'URL
  const { getTotalItems, hasDate, selectedDate, updateDate } = useCartStore();

  const [servicesSelected, setServicesSelected] = useState({});

  const fetchServiceData = useCallback(async () => {
    try {
      setFetchingData(true);
      const serviceRequestData = await getServiceById(service_id);
      if (serviceRequestData) {
        setServicesSelected(serviceRequestData);
      } else {
        const fallbackData = await getStoreService(service_id);
        setServicesSelected(fallbackData);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération du service :", error);
      try {
        const fallbackData = await getStoreService(service_id);
        setServicesSelected(fallbackData);
      } catch (fallbackError) {
        console.error(
          "Erreur lors du fallback avec getStoreService :",
          fallbackError
        );
        setServicesSelected({});
      }
    } finally {
      setFetchingData(false);
    }
  }, [service_id, getStoreService]);

  useEffect(() => {
    fetchServiceData();
  }, [fetchServiceData]);

  document.title = `${servicesSelected?.nom || ""} Services || Barberlys`;

  const handleDateTimeSelect = (formattedDateTime) => {
    // Si le panier a déjà une date, utiliser cette date
    if (hasDate()) {
      setSelectedDateTime(selectedDate);
    } else {
      setSelectedDateTime(formattedDateTime);
    }
    setShowReservationModal(true);
  };

  if (fetchingData) {
    return (
      <motion.div
        key="loading"
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 50 }}
        transition={{ duration: 0.4 }}
        className="h-screen flex justify-center items-center"
      >
        <div className="flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-t-4 border-b-4 border-white mr-1" />
          <span className="font-nunito text-[14px]">Chargement du service...</span>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen px-4 py-10 max-w-7xl mx-auto">
      {/* Titre */}
      <div className="mb-8">
        <h1 className="font-poppins font-semibold text-[18px] text-title mb-2">Réservation</h1>
        <Breadcrumb
          items={[
            { label: "Accueil", link: "/" },
            { label: "Services", link: "/services" },
            { label: `${servicesSelected?.nom || ""}`, link: "/services" },
          ]}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key="step2"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 50 }}
          transition={{ duration: 0.4 }}
        >
          {/* Détails du service */}
          <div className="bg-white p-6 shadow-md mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 items-start">
            <div>
              <div className="font-poppins font-medium text-[15px] text-gray-500 mb-2 flex gap-1 items-center">
                <CiCalendar className="" size={18} />
                <span>Détails de la réservation</span>
              </div>

              {servicesSelected?.category?.name && (
                <div className="flex justify-between items-center mb-3">
                  <span className="bg-gray-100 text-gray-600 font-nunito font-semibold text-[14px] px-3 py-1">
                    {servicesSelected.category.name}
                  </span>
                </div>
              )}

              <p className="font-poppins font-semibold text-[18px] text-title mb-2">
                {servicesSelected?.nom || "Service non trouvé"}
              </p>

              <p className="font-nunito text-[14px] text-text mb-4">
                {servicesSelected?.description ||
                  "Aucune description disponible"}
              </p>
            </div>

            <div className="flex flex-col gap-3">
              {servicesSelected?.price && (
                <div className="flex items-center justify-between p-3 bg-gray-50">
                  <span className="font-poppins font-medium text-[15px] text-text">Prix:</span>
                  <h3 className="font-poppins font-bold text-[16px] text-montant">
                    {servicesSelected.price} FCFA
                  </h3>
                </div>
              )}

              {/* {servicesSelected?.durationMinutes && (
                <div className="flex items-center justify-between p-3 bg-gray-50">
                  <span className="font-poppins font-medium text-[15px] text-text">Durée:</span>
                  <h3 className="font-poppins font-bold text-[16px] text-montant">
                    {servicesSelected.durationMinutes} minute
                    {servicesSelected.durationMinutes > 1 ? "s" : ""}
                  </h3>
                </div>
              )} */}
            </div>
          </div>

          {/* Calendrier de réservation */}
          <div className="mb-5">
            {hasDate() ? (
              <div className="bg-yellow-50 border border-yellow-200 p-4 mb-4">
                <div className="flex items-center justify-center">
                  <button
                    onClick={() => handleDateTimeSelect(selectedDate)}
                    className="px-4 py-2 bg-black text-white hover:bg-yellow-700 font-nunito font-semibold text-[14px]"
                  >
                    Ajouter au panier
                  </button>
                </div>
              </div>
            ) : null}

            <ReservationServiceCalendar
              servicesSelected={servicesSelected}
              onContinue={handleDateTimeSelect}
              disabled={hasDate()}
            />
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Modal de réservation */}
      
        <ReservationModal
        isOpen={showReservationModal}
        onClose={() => {
          setShowReservationModal(false);
          setSelectedDateTime("");
        }}
        service={servicesSelected}
        selectedDate={selectedDateTime}
      />
    </div>
  );
}